import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { DvDialogContent } from '@devinco/ui/dds';
import {
  DvCheckboxElement,
  DvCheckboxElementParams,
  DvDynamicFormElement,
  DvDynamicFormModule,
  DvFormUtils,
  DvTextboxElement,
  FormOf,
} from '@devinco/ui/legacy';
import { GroupType } from '@pages/statistics-page/constants';
import {
  ColoredStatisticsGroup,
  GroupSelectionForm,
} from '@pages/statistics-page/interfaces';
import { GroupsService } from '@pages/statistics-page/services/groups.service';
import { StatisticsMenuValuesService } from '@pages/statistics-page/services/statistics-menu-values.service';
import { PipesModule } from '@shared/pipes/pipes.module';
import { debounceTime, distinctUntilChanged, map, take } from 'rxjs';

@Component({
  imports: [DvDynamicFormModule, PipesModule],
  selector: 'app-group-selection-dialog',
  templateUrl: './group-selection-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./group-selection-dialog.component.scss'],
})
export class GroupSelectionDialogComponent
  extends DvDialogContent<undefined, GroupSelectionDialogComponent>
  implements OnInit
{
  private groupsService = inject(GroupsService);
  private formValuesService = inject(StatisticsMenuValuesService);

  selectAllForm: FormGroup<FormOf<Pick<GroupSelectionForm, 'selectAll'>>>;
  searchForm: FormGroup<FormOf<Pick<GroupSelectionForm, 'searchInput'>>>;
  selectAllControlElement: DvDynamicFormElement[] = [];
  searchControlElement: DvDynamicFormElement[] = [];
  groupsForm: FormGroup<FormOf<{ [key: string]: boolean }>>;
  groupsElements: DvDynamicFormElement[] = [];

  private allGroupsElements: DvDynamicFormElement[] = [];
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.setupFilterForm();
    this.setupGroupsForm();
  }

  performSave(): void {
    const selectedGroupsIds: string[] = Object.keys(
      Object.fromEntries(
        Object.entries(this.groupsForm.value).filter(([_, value]) => !!value)
      )
    );
    this.groupsService
      .getGroups()
      .pipe(take(1))
      .subscribe(allGroups => {
        const selectedGroups = allGroups.filter(group =>
          selectedGroupsIds.includes(String(group.id))
        );
        const globalGroup = allGroups.find(
          group => group.type === GroupType.Global
        ) as ColoredStatisticsGroup;
        this.formValuesService.pinnedGroups$.next([
          ...selectedGroups,
          globalGroup,
        ]);
      });
  }

  private listenToSelectAllToggling(): void {
    this.selectAllForm.controls.selectAll.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(checked => {
        Object.keys(this.groupsForm.controls).forEach(key => {
          if (
            this.groupsElements.find(element => element.options.key === key)
          ) {
            this.groupsForm.controls[key].setValue(checked);
          }
        });
      });
  }

  private setupFilterForm(): void {
    this.selectAllControlElement = [
      new DvCheckboxElement({
        colspan: 12,
        key: 'selectAll',
        checkboxLabel: 'Statistics.DepartmentSelectionModal.SelectAll',
      }),
    ];

    this.searchControlElement = [
      new DvTextboxElement({
        colspan: 12,
        key: 'searchInput',
      }),
    ];

    this.selectAllForm = DvFormUtils.createFormGroup(
      this.selectAllControlElement
    );

    this.searchForm = DvFormUtils.createFormGroup(this.searchControlElement);
    this.listenToSelectAllToggling();
    this.listenToSearchInput();
  }

  private setupGroupsForm(): void {
    this.groupsService
      .getGroups()
      .pipe(
        take(1),
        map(groups =>
          groups.map(group => ({
            ...group,
            label: `${group.idText ? group.idText + '-' : ''} ${
              group.description
            } (${group.vehicleCount})`,
          }))
        )
      )
      .subscribe(groups => {
        groups.forEach(group => {
          if (!group.hidden) {
            this.groupsElements.push(
              new DvCheckboxElement({
                colspan: 12,
                key: String(group.id),
                checkboxLabel: group.label,
              })
            );
          }
        });

        this.allGroupsElements = this.groupsElements;
        this.groupsForm = DvFormUtils.createFormGroup(this.groupsElements);
        this.setInitialGroupsFormValues();
      });
  }

  private listenToSearchInput(): void {
    this.searchForm.controls.searchInput.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(200),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(searchInput => {
        if (!searchInput) {
          this.groupsElements = this.allGroupsElements;
          this.toggleSelectAll();
          return;
        }
        const searchResult = this.allGroupsElements.filter(e => {
          return (e.options as DvCheckboxElementParams).checkboxLabel
            ?.toLowerCase()
            .includes(searchInput.toLowerCase());
        });

        this.groupsElements = searchResult;
        this.toggleSelectAll();
      });
  }

  private toggleSelectAll(): void {
    if (!this.groupsElements.length) return;

    const isEveryFilteredElementSelected = this.groupsElements.every(
      element => this.groupsForm.controls[element.options.key as string].value
    );

    isEveryFilteredElementSelected
      ? this.selectAllForm.controls.selectAll.setValue(true)
      : this.selectAllForm.controls.selectAll.setValue(false, {
          emitEvent: false,
        });
  }

  private setInitialGroupsFormValues(): void {
    const pinnedGroups = this.formValuesService.pinnedGroupsValue.filter(
      group => !group.hidden
    );

    if (!pinnedGroups.length) return;

    pinnedGroups.forEach(group => {
      this.groupsForm.controls[group.id].setValue(true, {
        emitEvent: false,
      });
    });

    // check select all if all groups selected
    if (pinnedGroups.length === Object.keys(this.groupsForm.controls).length) {
      this.selectAllForm.controls.selectAll.setValue(true, {
        emitEvent: false,
      });
    }
  }
}
